import { loadStripe } from '@stripe/stripe-js';
import { useRef } from 'react';

import stripeConfig, { resolveStripePublishableKey } from '@/config/stripe.config';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import auctionService from '@/services/auction.service';

import CollectorShippingForm from './CollectorShippingForm';

import './styles/VariableContent.css';

export default function VariableContent({
  fetchData,
  setOrderData,
  showShippingForm,
  setShowShippingForm,
  refetchOrder,
}) {
  const { state, stateHandler } = useStateHandler();
  const t = useTranslations('common.shared.order_details');
  const stripeInstanceRef = useRef(null);
  const checkoutInProgressRef = useRef(false);
  const currentUserId = state.user_session?._id ?? null;
  const orderBuyerId = fetchData?.order_buyer?._id ?? fetchData?.order_buyer ?? null;
  const isBuyer = Boolean(
    currentUserId &&
    orderBuyerId &&
    String(currentUserId) === String(orderBuyerId)
  );
  const paymentStatus = fetchData?.status ?? null;
  const orderType = fetchData?.type ?? null;
  const shippingStatus = fetchData?.order_shipping_address?.shipping_status
    ?? fetchData?.order_status
    ?? paymentStatus
    ?? null;
  const hasShippingAddress = Boolean(
    fetchData?.order_shipping_address?.address_name &&
    fetchData?.order_shipping_address?.address_street &&
    fetchData?.order_shipping_address?.address_country
  );
  const needsShippingAddress =
    isBuyer &&
    !hasShippingAddress &&
    (
      shippingStatus === "payment_pending" ||
      (paymentStatus === "paid" && orderType === "auction")
    );
  const orderId = fetchData?._id ?? null;

  const resolvedSetShowShippingForm =
    typeof setShowShippingForm === "function" ? setShowShippingForm : () => {};
  const shippingFormVisible = Boolean(showShippingForm);

  async function resolveStripeInstance() {
    if (stripeInstanceRef.current) {
      return stripeInstanceRef.current;
    }
    const publishableKey = stripeConfig.publishableKey || await resolveStripePublishableKey();
    if (!publishableKey) {
      return null;
    }
    const instance = await loadStripe(publishableKey);
    stripeInstanceRef.current = instance;
    return instance;
  }

  async function handleProceedToCheckout() {
    if (!orderId || checkoutInProgressRef.current) {
      return;
    }

    checkoutInProgressRef.current = true;

    try {
      const { response, data } = await auctionService.initiateOrderPaymentIntent(orderId);
      if (!response?.ok) {
        throw new Error(
          data?.error_message
          ?? data?.message
          ?? t('errors.checkout_start_failed')
        );
      }

      const paymentIntent = data?.payment_intent ?? null;
      const paymentStatusRaw = typeof paymentIntent?.status === "string"
        ? paymentIntent.status.toLowerCase()
        : "";
      const clientSecret =
        paymentIntent?.client_secret
        ?? data?.payment_authorization?.stripe_client_secret
        ?? null;

      if (clientSecret && paymentStatusRaw !== "succeeded") {
        const stripe = await resolveStripeInstance();
        if (!stripe) {
          throw new Error(t('errors.payment_form_unavailable'));
        }
        const confirmResult = await stripe.confirmCardPayment(clientSecret);
        if (confirmResult?.error) {
          throw confirmResult.error;
        }
      }

      if (typeof refetchOrder === "function") {
        try {
          await refetchOrder();
        }
        catch (refreshError) {
          console.warn("[OrderDetails] Unable to refresh order after payment", refreshError);
        }
      }
    }
    catch (error) {
      const fallbackMessage =
        error?.message
        ?? t('errors.checkout_failed');
      stateHandler.set("temporalPopup", {
        text: fallbackMessage,
      });
    }
    finally {
      checkoutInProgressRef.current = false;
    }
  }

  if (needsShippingAddress) {
    if (shippingFormVisible) {
      return (
        <CollectorShippingForm
          orderData={fetchData}
          onSuccess={(updatedOrder, shippingQuote) => {
            if (typeof setOrderData === 'function') {
              setOrderData((prev) => ({
                ...(prev ?? {}),
                ...updatedOrder,
                order_shipping_address: updatedOrder?.order_shipping_address ?? prev?.order_shipping_address,
                order_payment: updatedOrder?.order_payment ?? prev?.order_payment,
                order_shipping_quote: shippingQuote ?? prev?.order_shipping_quote ?? null,
              }));
            }
            resolvedSetShowShippingForm(false);
            const quoteAmount = Number(shippingQuote?.amount ?? updatedOrder?.order_payment?.shipping ?? 0);
            if (quoteAmount > 0) {
              handleProceedToCheckout();
            }
          }}
          onClose={() => resolvedSetShowShippingForm(false)}
        />
      );
    }

    return (
      <div className="order-variable-content__cta">
        <p className="order-variable-content__text">
          {t('shipping.address_required')}
        </p>
        <button
          type="button"
          className="order-variable-content__cta-button"
          onClick={() => resolvedSetShowShippingForm(true)}
        >
          {t('shipping.add_details')}
        </button>
      </div>
    );
  }

  if (shippingStatus === "pending") {
    if (fetchData.order_limited_edition_data) {
      return (
        <p className="order-variable-content__text">
          {t('shipping.limited_edition_pending')}
        </p>
      );
    }

    const currentUserIsSeller =
      String(state.user_session._id ?? "") ===
      String(fetchData?.order_seller?._id ?? fetchData?.order_seller ?? "");

    if (currentUserIsSeller) {
      return (
        <p className="order-variable-content__text">
          {t('shipping.seller_pending')}
        </p>
      );
    }

    if (orderType === "auction") {
      return null;
    }

    return (
      <p className="order-variable-content__text">
        {t('shipping.gallery_pending')}
      </p>
    );
  }

  if (shippingStatus === "sent") {
    return (
      <>
        <div className="order-variable-content__shipping-details">
          <p className="order-variable-content__gray-text">
            {t('shipping.tracking_number')}:
          </p>

          <p className="order-variable-content__big-text order-variable-content__big-text--spaced">
            {fetchData.order_tracking.tracking_number}
          </p>

          <p className="order-variable-content__gray-text">
            {t('shipping.courier')}:
          </p>

          <p className="order-variable-content__big-text">
            {fetchData.order_tracking.shipping_courier_company}
          </p>
        </div>

        <p className="order-variable-content__gray-text">
          {t('shipping.tracking_instruction')}
        </p>
      </>
    );
  }

  return null;
}
